import { track, trackInteraction } from 'frontend/shared/javascripts/tracking'
import abTests from 'frontend/shared/javascripts/modules/ab_tests'
import {
  trackAdBlockerNotification,
  type AdBlockerNotificationEvent,
} from 'frontend/shared/javascripts/util/server_side_tracking'
import { isSafari } from 'shared/javascripts/util/detection'
import mainCss from './css/huge_notification.sass?inline'
import mobileCss from './css/huge_notification_mobile.sass?inline'
import desktopCss from './css/huge_notification_desktop.sass?inline'
import allowlistCss from './css/huge_notification_allowlist.sass?inline'
import safariAllowlistUrl from 'frontend/shared/images/huge_notification/safari_allowlist.webp'
import safariStep1Url from 'frontend/shared/images/huge_notification/safari_step_1.webp'
import safariStep2Url from 'frontend/shared/images/huge_notification/safari_step_2.webp'
import safariStep3Url from 'frontend/shared/images/huge_notification/safari_step_3.webp'
import chromeStep1Url from 'frontend/shared/images/huge_notification/chrome_step_1.webp'
import chromeStep2Url from 'frontend/shared/images/huge_notification/chrome_step_2.webp'
import session from 'frontend/shared/javascripts/util/session'
import { inTestEnvironment } from 'shared/javascripts/util/environment'
import { emit, on } from 'shared/javascripts/util/events'

/**
 * Adblocker banner:
 * Since we have issues with some adblockers hiding our adblock notifications,
 * we add these banners client-side, and obfuscate all
 * HTML classes and data- attributes randomly.
 * CSS is also inserted client-side.
 * The obfuscation is super naive, and will simply replace
 * all words with a few given prefixes.
 *
 * We will have to watch whether this actually keeps ublock origin from
 * being able to hide our banner in the long run.
 */

const OBFUSCATE_WORDS_STARTING_WITH = ['huge-notification', '-mod-', 'data-']
// `data-convert-*` is set on <html> by Convert; the `if-ab-test` selectors in the CSS must keep matching it.
const SKIP_OBFUSCATE_WORDS_STARTING_WITH = ['data-env', 'data-convert']

const CLOSE_ICON = `<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g transform="translate(4.015 3.515)" fill="currentColor" fill-rule="nonzero"><polygon points="15.556 0 16.971 1.414 9.9 8.485 16.971 15.556 15.556 16.971 8.485 9.9 1.414 16.971 0 15.556 7.071 8.485 0 1.414 1.414 0 8.485 7.071"></polygon></g></svg>`

const MOBILE_HTML = `
<div class="huge-notification-mobile">
  <button class="huge-notification--close" data-close aria-label="Schließen">
    ${CLOSE_ICON}
  </button>
  <div class="huge-notification-mobile--body">
    <p><strong>Hallo, leider nutzt du einen <span class="text-danger">AdBlocker.</span></strong></p>
    <p>Auf Studyflix bieten wir dir kostenlos hochwertige Bildung an. Dies können wir nur durch die Unterstützung unserer Werbepartner tun.</p>
    <p><strong>Schalte bitte deinen Adblocker für Studyflix aus oder füge uns zu deinen Ausnahmen hinzu.</strong> Das tut dir nicht weh und hilft uns weiter.</p>
    <p>Danke!<br>Dein Studyflix-Team</p>
    <p class="huge-notification-mobile--subline">Bitte <button class="huge-notification-mobile--reload" data-reload>lade anschließend die Seite neu</button>.</p>
  </div>
</div>`

const DESKTOP_HTML = `
<div class="huge-notification-desktop -mod-on-step-1" data-stepper>
  <button class="huge-notification--close" data-close aria-label="Schließen">
    ${CLOSE_ICON}
  </button>
  <div class="huge-notification-desktop--body">
    <div class="huge-notification-desktop--layout">
      <div class="huge-notification-desktop--image-col">
        <div class="huge-notification-desktop--image"></div>
      </div>
      <div class="huge-notification-desktop--text-col">
        <div class="huge-notification-desktop--step -mod-step-1">
          <p class="huge-notification-desktop--headline -mod-step-1">Bitte deaktiviere deinen <span class="text-danger">AdBlocker</span>.</p>
          <p>Studyflix zeigt dir beim Lernen passende Ausbildungsplätze, Studienmöglichkeiten und Jobs, die zu dir und deinen Interessen passen.</p>
          <p><strong>Schalte deinen Adblocker für Studyflix aus oder füge uns zu deinen Ausnahmen hinzu.</strong></p>
          <button class="huge-notification-desktop--button btn btn-primary" data-open-guide>Adblocker deaktivieren – so geht's</button>
          <div class="huge-notification-desktop--divider"></div>
          <p class="huge-notification-desktop--recheck">
            Schon erledigt?
            <button class="btn btn-outline-primary" data-reload>Seite neu laden</button>
          </p>
        </div>
        <div class="huge-notification-desktop--step -mod-step-2-safari">
          <p class="huge-notification-desktop--headline">Adblocker deaktivieren in vier einfachen Schritten</p>
          <div class="huge-notification-desktop--steps">
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">1</div>
                <div>Klicke in der Programmleiste auf <strong>Safari</strong> und dann auf <strong>Safari-Erweiterungen</strong>...</div>
              </div>
              <img src="${safariStep1Url}" width="345" height="82">
            </div>
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">2</div>
                <div>Öffne deine <strong>Adblocker-Einstellungen</strong>.</div>
              </div>
              <img src="${safariStep2Url}" width="282" height="58">
            </div>
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">3</div>
                <div>Füge eine Ausnahme für <strong>studyflix.de</strong> hinzu und speichere die Einstellungen.</div>
              </div>
              <img src="${safariStep3Url}" width="640" height="140">
            </div>
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">4</div>
                <div>Prima. <strong>Lade jetzt die Seite neu!</strong></div>
              </div>
              <button class="btn btn-outline-primary" data-reload>Seite neu laden</button>
            </div>
          </div>
        </div>
        <div class="huge-notification-desktop--step -mod-step-2-non-safari">
          <p class="huge-notification-desktop--headline">Adblocker deaktivieren in drei einfachen Schritten</p>
          <div class="huge-notification-desktop--steps">
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">1</div>
                <div>Klicke in der Browserleiste auf das <strong>Erweiterungen-Symbol</strong> und öffne deine <strong>Adblocker-Einstellungen</strong>.</div>
              </div>
              <img src="${chromeStep1Url}" width="347" height="124">
            </div>
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">2</div>
                <div><strong>Deaktiviere</strong> den Adblocker <strong>für Studyflix.de</strong>.</div>
              </div>
              <img src="${chromeStep2Url}" width="407" height="180">
            </div>
            <div class="huge-notification-desktop--step-item">
              <div class="huge-notification-desktop--instruction">
                <div class="huge-notification-desktop--instruction-number">3</div>
                <div>Prima. <strong>Lade jetzt die Seite neu!</strong></div>
              </div>
              <button class="btn btn-outline-primary" data-reload>Seite neu laden</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`

const SHIELD_ICON = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>`
const CLOCK_ICON = `<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M8.75 11.8751H13.3333V10.6251H10V5.41675L8.75 5.41675V11.8751Z"></path><path fill-rule="evenodd" clip-rule="evenodd" d="M18.3333 10.0001C18.3333 5.39771 14.6024 1.66675 10 1.66675C5.39763 1.66675 1.66667 5.39771 1.66667 10.0001C1.66667 14.6025 5.39763 18.3334 10 18.3334C14.6024 18.3334 18.3333 14.6025 18.3333 10.0001ZM3.33334 10.0001C3.33334 6.31818 6.31811 3.33341 10 3.33341C13.6819 3.33341 16.6667 6.31818 16.6667 10.0001C16.6667 13.682 13.6819 16.6667 10 16.6667C6.31811 16.6667 3.33334 13.682 3.33334 10.0001Z"></path></svg>`
const CHECK_ICON = `<svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.1378 5.13812L5.99972 12.2762L2.52832 8.80479L3.47113 7.86198L5.99972 10.3906L12.195 4.19531L13.1378 5.13812Z"></path></svg>`
const BACK_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 6l-6 6 6 6"></path></svg>`

const allowlistInstruction = (number: number, text: string) => `
  <div class="huge-notification-allowlist--instruction">
    <div class="huge-notification-allowlist--number">${number}</div>
    <div>${text}</div>
  </div>`

const ALLOWLIST_STEP_2 = allowlistInstruction(
  2,
  `Schalte den Adblocker für <strong>studyflix.de</strong> aus. Auf allen anderen Seiten bleibt er an.`,
)

// Variant b of the adblock_allowlisting experiment. It is rendered next to the control's DESKTOP_HTML, and the
// `if-ab-test` rules in the stylesheets show one of the two, following Convert's decision live.
const ALLOWLIST_HTML = `
<div class="huge-notification-allowlist -mod-on-step-1" data-stepper>
  <button class="huge-notification--close" data-close aria-label="Schließen">
    ${CLOSE_ICON}
  </button>
  <div class="huge-notification-allowlist--hint">
    <div class="huge-notification-allowlist--figure-col">
      <div class="huge-notification-allowlist--figure"></div>
    </div>
    <div class="huge-notification-allowlist--hint-text">
      <p class="huge-notification-allowlist--headline -mod-balanced">Erlaube Werbung nur auf Studyflix – dein Adblocker bleibt überall sonst an.</p>
      <p>Bei uns findest du passende Ausbildungsplätze, Studienmöglichkeiten und Jobs – und das Wichtigste: Lernen mit Studyflix bleibt für dich kostenlos.</p>
      <ul class="huge-notification-allowlist--points">
        <li>${SHIELD_ICON} Überall sonst bleibt Werbung blockiert</li>
        <li>${CLOCK_ICON} Dauert ca. 10 Sekunden</li>
        <li>${CHECK_ICON} Jederzeit umkehrbar</li>
      </ul>
      <div class="huge-notification-allowlist--actions">
        <button class="huge-notification-allowlist--button btn btn-primary" data-open-guide>Studyflix erlauben – so geht's</button>
        <p>Schon erledigt? <button class="huge-notification-allowlist--link" data-reload>Seite neu laden</button></p>
      </div>
    </div>
  </div>
  <div class="huge-notification-allowlist--guide">
    <button class="huge-notification-allowlist--link -mod-back" data-back>${BACK_ICON} Zurück</button>
    <p class="huge-notification-allowlist--headline">In wenigen Schritten erledigt – nur für Studyflix</p>
    <div class="huge-notification-allowlist--steps -mod-chrome">
      <div class="huge-notification-allowlist--step">
        <div class="huge-notification-allowlist--screenshot">
          <img src="${chromeStep1Url}" width="347" height="124" loading="lazy" alt="Erweiterungen-Symbol in der Browserleiste mit geöffnetem Adblocker-Eintrag">
        </div>
        ${allowlistInstruction(1, 'Klick oben rechts im Browser auf das <strong>Symbol deines Adblockers</strong>.')}
      </div>
      <div class="huge-notification-allowlist--step">
        <div class="huge-notification-allowlist--screenshot">
          <img src="${chromeStep2Url}" width="407" height="180" loading="lazy" alt="Adblocker-Menü mit Schalter für diese Webseite">
        </div>
        ${ALLOWLIST_STEP_2}
      </div>
    </div>
    <div class="huge-notification-allowlist--steps -mod-safari">
      <div class="huge-notification-allowlist--screenshot">
        <img src="${safariAllowlistUrl}" width="1199" height="560" loading="lazy" alt="Geöffnetes Adblocker-Menü in Safari mit Schalter für studyflix.de">
      </div>
      <div class="huge-notification-allowlist--instructions">
        ${allowlistInstruction(1, 'Klick oben in der Safari-Symbolleiste auf das <strong>Symbol deines Adblockers</strong>.')}
        ${ALLOWLIST_STEP_2}
      </div>
    </div>
    <div class="huge-notification-allowlist--actions">
      <button class="huge-notification-allowlist--button btn btn-primary" data-reload>Fertig – Seite neu laden</button>
      <p>
        Anderer Browser?
        <button class="huge-notification-allowlist--link -mod-chrome" data-switch-guide>Anleitung für Safari</button>
        <button class="huge-notification-allowlist--link -mod-safari" data-switch-guide>Anleitung für Chrome, Edge &amp; Firefox</button>
      </p>
    </div>
  </div>
</div>`

type Guide = 'chrome' | 'safari'
type Screen = 'hint' | 'guide'

const HINT_STEP = '-mod-on-step-1'
const GUIDE_STEPS: Record<Guide, string> = {
  chrome: '-mod-on-step-2-non-safari',
  safari: '-mod-on-step-2-safari',
}

let mounted = false
let guide: Guide | undefined

const seed = Math.random().toString(36).slice(2)

const obfuscateWord = (word: string) => {
  // this works something like a cryptographic hash
  if (SKIP_OBFUSCATE_WORDS_STARTING_WITH.some((prefix) => word.startsWith(prefix))) {
    return word
  }
  let h = 0
  for (const c of word + seed) h = (Math.imul(31, h) + c.charCodeAt(0)) | 0
  return 'a' + Math.abs(h).toString(36)
}

function obfuscateHTML(s: string) {
  for (const prefix of OBFUSCATE_WORDS_STARTING_WITH) {
    s = s.replaceAll(new RegExp(`${prefix}[a-zA-Z0-9_-]*`, 'g'), obfuscateWord)
  }
  return s
}

function obfuscatedAttributeSelector(selector: string) {
  return `[${obfuscateWord(selector)}]`
}

function onDetected() {
  if (session.academyPartnerName) return
  if (mounted) return
  mounted = true

  const host = document.createElement('div')
  if (inTestEnvironment()) {
    host.setAttribute('data-testid', 'adblock-notification')
  }

  host.innerHTML = obfuscateHTML(
    `<style>${mainCss}${mobileCss}${desktopCss}${allowlistCss}</style>
<div class="huge-notification--container">
  <div class="huge-notification" data-notification>
    ${MOBILE_HTML}
    ${DESKTOP_HTML}
    ${ALLOWLIST_HTML}
  </div>
</div>`,
  )
  document.body.appendChild(host)

  const notification = host.querySelector<HTMLElement>(
    obfuscatedAttributeSelector('data-notification'),
  )!

  const onClick = (attribute: string, handler: (button: HTMLElement) => void) => {
    host.querySelectorAll<HTMLElement>(obfuscatedAttributeSelector(attribute)).forEach((button) => {
      button.addEventListener('click', () => handler(button))
    })
  }

  onClick('data-close', (button) => {
    const screen = screenOf(button)
    // tracking: adblock_notification.closed
    track('adBlocker.hideNotification', { screen })
    trackServerSideWithVariant('ad_blocker_notification_closed', { screen })
    notification.classList.add(obfuscateWord('-mod-minimized'))
    emit('app.adBlocker.notificationHidden')
  })

  onClick('data-reload', (button) => {
    // tracking: adblock_notification.reload
    trackClick(button, 'adblock_notification_reload', 'ad_blocker_reload_clicked', {
      screen: screenOf(button),
    })
    location.reload()
  })

  onClick('data-open-guide', (button) => {
    guide ??= isSafari() ? 'safari' : 'chrome'
    showStep(button, GUIDE_STEPS[guide])
    stepperOf(button)!.classList.add(obfuscateWord('-mod-grown'))
    // tracking: adblock_notification.guide_open
    trackClick(button, 'adblock_notification_guide_open', 'ad_blocker_guide_opened')
  })

  onClick('data-back', (button) => {
    showStep(button, HINT_STEP)
    // tracking: adblock_notification.guide_back
    trackClick(button, 'adblock_notification_guide_back', 'ad_blocker_guide_back')
  })

  onClick('data-switch-guide', (button) => {
    guide = guide === 'safari' ? 'chrome' : 'safari'
    showStep(button, GUIDE_STEPS[guide])
    // tracking: adblock_notification.guide_switch
    trackClick(button, 'adblock_notification_guide_switch', 'ad_blocker_guide_switched', { guide })
  })

  // Give adblockers a moment to hide or remove the notification before we check
  // whether the user actually sees it.
  setTimeout(() => {
    if (isVisible(notification)) {
      // tracking: adblock_notification.shown
      track('adBlocker.showNotification')
      trackServerSideWithVariant('ad_blocker_notification_shown')
      // Lets later banners (the survey) wait until this one is out of the way.
      emit('app.adBlocker.notificationShown')
    } else {
      // tracking: adblock_notification.shown_failed
      track('adBlocker.showNotificationFailed')
    }
  }, 200)
}

// The desktop layouts are steppers; the mobile banner has a single screen and none.
function stepperOf(element: HTMLElement) {
  return element.closest<HTMLElement>(obfuscatedAttributeSelector('data-stepper'))
}

function showStep(button: HTMLElement, step: string) {
  const stepper = stepperOf(button)!
  for (const modifier of [HINT_STEP, ...Object.values(GUIDE_STEPS)]) {
    stepper.classList.toggle(obfuscateWord(modifier), modifier === step)
  }
}

function screenOf(element: HTMLElement): Screen {
  const stepper = stepperOf(element)
  return stepper && !stepper.classList.contains(obfuscateWord(HINT_STEP)) ? 'guide' : 'hint'
}

function trackClick(
  button: HTMLElement,
  id: string,
  serverEvent: AdBlockerNotificationEvent,
  params: Record<string, string> = {},
) {
  trackInteraction('click.element', { id, params }, button)
  trackServerSideWithVariant(serverEvent, params)
}

// GA4 can't learn the Convert variant from a client whose ad blocker blocks gtag, so the server-side events carry it.
function trackServerSideWithVariant(
  event: AdBlockerNotificationEvent,
  params: Record<string, string> = {},
) {
  const abVariant = abTests.variantCurrentlyActive('adblock_allowlisting', 'b') ? 'b' : 'a'
  trackAdBlockerNotification(event, { ab_variant: abVariant, ...params })
}

function isVisible(element: HTMLElement): boolean {
  if (!element.isConnected) return false
  const { width, height } = element.getBoundingClientRect()
  if (width === 0 || height === 0) return false
  return getComputedStyle(element).visibility !== 'hidden'
}

// Both frontends emit this: the player's adblock detection runs wherever the player does.
on('app.adBlocker.detected', () => onDetected())
